import { useState, useEffect, useCallback, useRef } from 'react';
import {
  Plus, Search, X, Check, Edit3, Upload, Download, Trash2,
  FileText, Presentation, FileArchive,
} from 'lucide-react';
import api from '../utils/api';
import PageHeader from '../components/PageHeader';

const CATEGORIES = ['기획안', '제안서', '운영안', '결과보고'];

const CAT_COLORS = {
  '기획안':  'bg-violet-900/50 text-violet-300',
  '제안서':  'bg-blue-900/50   text-blue-300',
  '운영안':  'bg-emerald-900/50 text-emerald-300',
  '결과보고': 'bg-amber-900/50  text-amber-300',
};

function fmtSize(b) {
  if (!b) return '';
  if (b < 1024) return `${b}B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)}KB`;
  return `${(b / 1024 / 1024).toFixed(1)}MB`;
}

// ─── 업로드 모달 ───────────────────────────────────────────
function UploadModal({ onClose, onUploaded }) {
  const [title, setTitle]       = useState('');
  const [category, setCategory] = useState('기획안');
  const [origFile, setOrigFile] = useState(null);
  const [pdfFile, setPdfFile]   = useState(null);
  const [saving, setSaving]     = useState(false);

  async function handleSubmit(e) {
    e.preventDefault();
    if (!title.trim()) return alert('제목을 입력하세요');
    if (!origFile && !pdfFile) return alert('파일을 하나 이상 업로드하세요');
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append('title', title.trim());
      fd.append('category', category);
      if (origFile) fd.append('originalFile', origFile);
      if (pdfFile)  fd.append('pdfFile', pdfFile);
      const { data } = await api.post('/proposals', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      onUploaded(data);
    } catch { alert('업로드 실패'); }
    setSaving(false);
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
      <div className="bg-gray-900 border border-gray-700 rounded-2xl shadow-2xl w-[480px]">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-800">
          <h3 className="text-sm font-semibold text-white">문서 업로드</h3>
          <button onClick={onClose} className="text-gray-500 hover:text-white"><X size={16} /></button>
        </div>
        <form onSubmit={handleSubmit} className="px-5 py-4 flex flex-col gap-4">
          <div>
            <label className="text-xs text-gray-400 mb-1.5 block">카테고리 *</label>
            <div className="flex gap-2 flex-wrap">
              {CATEGORIES.map(cat => (
                <button key={cat} type="button" onClick={() => setCategory(cat)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors border ${
                    category === cat
                      ? 'bg-brand text-white border-brand'
                      : 'border-gray-700 text-gray-400 hover:border-gray-500 hover:text-gray-200'
                  }`}>
                  {cat}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="text-xs text-gray-400 mb-1.5 block">제목 *</label>
            <input value={title} onChange={e => setTitle(e.target.value)} placeholder="문서 제목"
              className="w-full bg-gray-800 border border-gray-700 rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:border-brand" />
          </div>
          <div>
            <label className="text-xs text-gray-400 mb-1.5 block">원본 파일 (PPT, HWP 등)</label>
            <input type="file" accept=".ppt,.pptx,.hwp,.hwpx,.doc,.docx,.key,.odp"
              onChange={e => setOrigFile(e.target.files[0] || null)}
              className="w-full text-sm text-gray-300 file:mr-3 file:py-1.5 file:px-3 file:rounded-lg file:border-0 file:bg-gray-700 file:text-gray-300 file:text-xs hover:file:bg-gray-600 cursor-pointer" />
            {origFile && <p className="text-xs text-gray-500 mt-1">{origFile.name} · {fmtSize(origFile.size)}</p>}
          </div>
          <div>
            <label className="text-xs text-gray-400 mb-1.5 block">PDF 파일</label>
            <input type="file" accept=".pdf"
              onChange={e => setPdfFile(e.target.files[0] || null)}
              className="w-full text-sm text-gray-300 file:mr-3 file:py-1.5 file:px-3 file:rounded-lg file:border-0 file:bg-gray-700 file:text-gray-300 file:text-xs hover:file:bg-gray-600 cursor-pointer" />
            {pdfFile && <p className="text-xs text-gray-500 mt-1">{pdfFile.name} · {fmtSize(pdfFile.size)}</p>}
          </div>
          <div className="flex justify-end gap-2 pt-1">
            <button type="button" onClick={onClose}
              className="px-4 py-2 rounded-xl text-sm text-gray-400 hover:text-white border border-gray-700 hover:border-gray-500 transition-colors">취소</button>
            <button type="submit" disabled={saving}
              className="flex items-center gap-1.5 px-4 py-2 bg-brand text-white rounded-xl text-sm hover:bg-brand/80 disabled:opacity-50 transition-colors">
              <Upload size={14} /> {saving ? '업로드 중...' : '업로드'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── 미리보기 ───────────────────────────────────────────────
function ProposalPreview({ proposal, onDeleted, onTitleChanged }) {
  const [pdfBlobUrl, setPdfBlobUrl]     = useState(null);
  const [loadError, setLoadError]       = useState(false);
  const [editingTitle, setEditingTitle] = useState(false);
  const [titleInput, setTitleInput]     = useState(proposal.title);
  const titleRef = useRef(null);

  useEffect(() => {
    setPdfBlobUrl(null); setLoadError(false);
    if (!proposal.pdfFileUrl) return;
    let url;
    api.get(`/proposals/${proposal.id}/pdf/download`, { responseType: 'blob' })
      .then(r => { url = URL.createObjectURL(r.data); setPdfBlobUrl(url); })
      .catch(() => setLoadError(true));
    return () => { if (url) URL.revokeObjectURL(url); };
  }, [proposal.id, proposal.pdfFileUrl]);

  function downloadOrig() {
    api.get(`/proposals/${proposal.id}/original/download`, { responseType: 'blob' })
      .then(r => {
        const url = URL.createObjectURL(r.data);
        const a = document.createElement('a');
        a.href = url; a.download = proposal.originalFileName; a.click();
        URL.revokeObjectURL(url);
      }).catch(() => alert('다운로드 실패'));
  }

  function downloadPdf() {
    api.get(`/proposals/${proposal.id}/pdf/download`, { responseType: 'blob' })
      .then(r => {
        const url = URL.createObjectURL(r.data);
        const a = document.createElement('a');
        a.href = url; a.download = proposal.pdfFileName; a.click();
        URL.revokeObjectURL(url);
      }).catch(() => alert('다운로드 실패'));
  }

  function handleDelete() {
    if (!window.confirm('삭제하시겠습니까?')) return;
    api.delete(`/proposals/${proposal.id}`)
      .then(() => onDeleted(proposal.id))
      .catch(() => alert('삭제 실패'));
  }

  function startEdit() {
    setTitleInput(proposal.title); setEditingTitle(true);
    setTimeout(() => titleRef.current?.select(), 0);
  }
  function cancelEdit() { setEditingTitle(false); setTitleInput(proposal.title); }
  function saveTitle() {
    const t = titleInput.trim();
    if (!t) { cancelEdit(); return; }
    if (t === proposal.title) { setEditingTitle(false); return; }
    api.patch(`/proposals/${proposal.id}`, { title: t })
      .then(r => { onTitleChanged?.(r.data); setEditingTitle(false); })
      .catch(() => alert('저장 실패'));
  }

  return (
    <div className="flex flex-col h-full overflow-hidden">
      {/* 툴바 */}
      <div className="flex items-center justify-between px-2 py-2 flex-shrink-0 border-b border-gray-800/60">
        <div className="min-w-0 mr-3 flex items-center gap-2 flex-1">
          <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium flex-shrink-0 ${CAT_COLORS[proposal.category] || 'bg-gray-700 text-gray-300'}`}>
            {proposal.category}
          </span>
          {editingTitle ? (
            <>
              <input ref={titleRef} value={titleInput}
                onChange={e => setTitleInput(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') saveTitle(); if (e.key === 'Escape') cancelEdit(); }}
                className="flex-1 min-w-0 bg-gray-800 border border-gray-600 rounded px-2 py-1 text-sm text-white outline-none focus:border-brand" />
              <button onClick={saveTitle} className="text-green-400 hover:text-green-300 p-1"><Check size={14} /></button>
              <button onClick={cancelEdit} className="text-gray-500 hover:text-gray-300 p-1"><X size={14} /></button>
            </>
          ) : (
            <>
              <span className="text-sm font-medium text-white truncate">{proposal.title}</span>
              <button onClick={startEdit} className="text-gray-600 hover:text-gray-300 flex-shrink-0 p-0.5"><Edit3 size={12} /></button>
            </>
          )}
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {proposal.originalFileName && (
            <button onClick={downloadOrig}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-gray-700 text-xs text-gray-400 hover:text-white hover:border-gray-500 transition-colors">
              <FileArchive size={13} /> 원본
            </button>
          )}
          {proposal.pdfFileName && (
            <button onClick={downloadPdf}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-gray-700 text-xs text-gray-400 hover:text-white hover:border-gray-500 transition-colors">
              <Download size={13} /> PDF
            </button>
          )}
          <button onClick={handleDelete}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-red-900/50 text-xs text-red-500 hover:bg-red-900/20 transition-colors">
            <Trash2 size={13} /> 삭제
          </button>
        </div>
      </div>

      {/* 본문 */}
      <div className="flex-1 overflow-hidden flex flex-col">
        {proposal.pdfFileUrl && !loadError && !pdfBlobUrl && (
          <div className="flex items-center justify-center flex-1 text-xs text-gray-600">PDF 불러오는 중...</div>
        )}
        {proposal.pdfFileUrl && pdfBlobUrl && (
          <iframe src={pdfBlobUrl} className="w-full flex-1 border-0" title={proposal.title} />
        )}
        {proposal.pdfFileUrl && loadError && (
          <div className="flex flex-col items-center justify-center flex-1 gap-4 text-gray-500">
            <FileText size={48} className="opacity-20" />
            <p className="text-sm text-gray-400">PDF 미리보기를 불러올 수 없습니다.</p>
            <button onClick={downloadPdf}
              className="flex items-center gap-2 px-5 py-2.5 bg-brand text-white rounded-xl hover:bg-brand/80 text-sm">
              <Download size={15} /> PDF 다운로드
            </button>
          </div>
        )}
        {!proposal.pdfFileUrl && proposal.originalFileName && (
          <div className="flex flex-col items-center justify-center flex-1 gap-4 text-gray-500">
            <Presentation size={52} className="opacity-20" />
            <div className="text-center">
              <p className="text-sm font-medium text-gray-300">{proposal.title}</p>
              <p className="text-xs text-gray-600 mt-1">{proposal.originalFileName} · {fmtSize(proposal.originalSize)}</p>
            </div>
            <button onClick={downloadOrig}
              className="flex items-center gap-2 px-5 py-2.5 bg-brand text-white rounded-xl hover:bg-brand/80 text-sm">
              <Download size={15} /> 원본 다운로드
            </button>
          </div>
        )}
        {!proposal.pdfFileUrl && !proposal.originalFileName && (
          <div className="flex items-center justify-center flex-1 text-xs text-gray-600">업로드된 파일 없음</div>
        )}
      </div>
    </div>
  );
}

// ─── 메인 페이지 ────────────────────────────────────────────
export default function ProposalPage() {
  const [list, setList]             = useState([]);
  const [selected, setSelected]     = useState(null);
  const [loading, setLoading]       = useState(false);
  const [search, setSearch]         = useState('');
  const [showUpload, setShowUpload] = useState(false);

  const load = useCallback(async (autoSelect = false) => {
    setLoading(true);
    try {
      const { data } = await api.get('/proposals');
      setList(data);
      if (autoSelect && data.length > 0) setSelected(data[0]);
    } catch {}
    setLoading(false);
  }, []);

  useEffect(() => { load(true); }, [load]);

  const filtered = list.filter(p =>
    !search || p.title.toLowerCase().includes(search.toLowerCase())
  );

  function handleUploaded(proposal) {
    setShowUpload(false);
    setList(prev => [proposal, ...prev]);
    setSelected(proposal);
  }

  function handleDeleted(id) {
    setList(prev => prev.filter(p => p.id !== id));
    if (selected?.id === id) setSelected(null);
  }

  function handleTitleChanged(updated) {
    setList(prev => prev.map(p => p.id === updated.id ? { ...p, ...updated } : p));
    setSelected(prev => prev?.id === updated.id ? { ...prev, ...updated } : prev);
  }

  return (
    <div className="flex flex-col h-full">
      <PageHeader icon={Presentation} title="기획안" count={list.length}>
        <button onClick={() => setShowUpload(true)}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-brand text-white text-sm rounded-lg hover:bg-brand/80">
          <Plus size={14} /> 업로드
        </button>
      </PageHeader>

      <div className="flex flex-1 min-h-0 gap-4 overflow-hidden">
        {/* 좌측 목록 */}
        <div className="w-64 flex-shrink-0 flex flex-col gap-3">
          <div className="flex items-center bg-gray-900 border border-gray-800 rounded-xl px-2.5">
            <Search size={13} className="text-gray-500 flex-shrink-0" />
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="검색..."
              className="flex-1 bg-transparent text-white text-xs py-2 px-2 focus:outline-none placeholder-gray-600" />
            {search && <button onClick={() => setSearch('')} className="text-gray-600 hover:text-gray-400"><X size={12} /></button>}
          </div>
          <div className="flex-1 overflow-y-auto flex flex-col gap-1">
            {loading && <div className="text-center py-8 text-xs text-gray-600">불러오는 중...</div>}
            {!loading && filtered.length === 0 && (
              <div className="text-center py-8 text-xs text-gray-600">
                {search ? '검색 결과 없음' : '문서를 업로드하세요'}
              </div>
            )}
            {filtered.map(p => (
              <button key={p.id} onClick={() => setSelected(p)}
                className={`w-full text-left px-3 py-2.5 rounded-xl border transition-all ${selected?.id === p.id ? 'bg-brand/20 border-brand/30' : 'border-transparent hover:bg-gray-800'}`}>
                <div className="flex items-center gap-1.5 mb-1">
                  <span className={`text-[9px] px-1.5 py-0.5 rounded font-medium flex-shrink-0 ${CAT_COLORS[p.category] || 'bg-gray-700 text-gray-300'}`}>
                    {p.category}
                  </span>
                  <span className="text-xs font-medium text-white truncate">{p.title}</span>
                </div>
                <div className="flex items-center gap-1.5">
                  {p.pdfFileUrl     && <span className="text-[9px] bg-gray-700 text-gray-300 px-1.5 py-0.5 rounded">PDF</span>}
                  {p.originalFileUrl && <span className="text-[9px] bg-gray-700 text-gray-300 px-1.5 py-0.5 rounded">원본</span>}
                  <span className="text-[10px] text-gray-500">{p.createdAt?.slice(0, 10)}</span>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* 우측 패널 */}
        <div className="flex-1 min-w-0 overflow-hidden">
          {!selected ? (
            <div className="flex flex-col items-center justify-center h-full text-gray-600 gap-3">
              <Presentation size={40} className="opacity-30" />
              <p className="text-sm">문서를 선택하거나 업로드하세요.</p>
            </div>
          ) : (
            <ProposalPreview
              proposal={selected}
              onDeleted={handleDeleted}
              onTitleChanged={handleTitleChanged}
            />
          )}
        </div>
      </div>

      {showUpload && <UploadModal onClose={() => setShowUpload(false)} onUploaded={handleUploaded} />}
    </div>
  );
}
